/**
 * 统计卡片组件 + 统计卡片网格组件
 *
 * Dashboard 中常用的统计数据展示卡片，支持图标、数值、标签、颜色预设。
 */

import type { GlobalToken } from 'antd';
import { Card, Col, Row, Skeleton, theme } from 'antd';
import React, { useMemo } from 'react';

/**
 * 颜色预设 — 通过 antd token 生成，自动适配暗黑模式
 *
 * 运行时通过 `getThemedColorPresets(token)` 获取当前主题对应的颜色值。
 * 静态常量 `STAT_COLOR_PRESETS` 仅作为浅色模式的回退值使用。
 */
export const STAT_COLOR_PRESETS = {
  blue: { bg: '#e6f4ff', text: '#1677ff', icon: '#1677ff' },
  green: { bg: '#f6ffed', text: '#52c41a', icon: '#52c41a' },
  orange: { bg: '#fff7e6', text: '#fa8c16', icon: '#fa8c16' },
  red: { bg: '#fff2f0', text: '#ff4d4f', icon: '#ff4d4f' },
  purple: { bg: '#f9f0ff', text: '#722ed1', icon: '#722ed1' },
  cyan: { bg: '#e6fffb', text: '#13c2c2', icon: '#13c2c2' },
  gold: { bg: '#fffbe6', text: '#faad14', icon: '#faad14' },
  lime: { bg: '#fcffe6', text: '#a0d911', icon: '#a0d911' },
} as const;

/**
 * 安全读取 antd token 扩展色板字段
 *
 * antd v5 GlobalToken 类型未声明 purple1/cyan1 等扩展色板字段，
 * 但运行时它们确实存在于 token 对象中。使用安全索引访问 + 回退值
 * 避免强制类型断言，提高版本兼容性。
 */
function getExtendedTokenColor(
  token: GlobalToken,
  key: string,
  fallback: string,
): string {
  const value = (token as unknown as Record<string, unknown>)[key];
  return typeof value === 'string' && value ? value : fallback;
}

function getThemedColorPresets(token: GlobalToken) {
  return {
    blue: {
      bg: token.colorInfoBg || STAT_COLOR_PRESETS.blue.bg,
      text: token.colorInfo || STAT_COLOR_PRESETS.blue.text,
      icon: token.colorInfo || STAT_COLOR_PRESETS.blue.icon,
    },
    green: {
      bg: token.colorSuccessBg || STAT_COLOR_PRESETS.green.bg,
      text: token.colorSuccess || STAT_COLOR_PRESETS.green.text,
      icon: token.colorSuccess || STAT_COLOR_PRESETS.green.icon,
    },
    orange: {
      bg: token.colorWarningBg || STAT_COLOR_PRESETS.orange.bg,
      text: token.colorWarning || STAT_COLOR_PRESETS.orange.text,
      icon: token.colorWarning || STAT_COLOR_PRESETS.orange.icon,
    },
    red: {
      bg: token.colorErrorBg || STAT_COLOR_PRESETS.red.bg,
      text: token.colorError || STAT_COLOR_PRESETS.red.text,
      icon: token.colorError || STAT_COLOR_PRESETS.red.icon,
    },
    purple: {
      bg: getExtendedTokenColor(token, 'purple1', STAT_COLOR_PRESETS.purple.bg),
      text: getExtendedTokenColor(
        token,
        'purple6',
        STAT_COLOR_PRESETS.purple.text,
      ),
      icon: getExtendedTokenColor(
        token,
        'purple6',
        STAT_COLOR_PRESETS.purple.icon,
      ),
    },
    cyan: {
      bg: getExtendedTokenColor(token, 'cyan1', STAT_COLOR_PRESETS.cyan.bg),
      text: getExtendedTokenColor(token, 'cyan6', STAT_COLOR_PRESETS.cyan.text),
      icon: getExtendedTokenColor(token, 'cyan6', STAT_COLOR_PRESETS.cyan.icon),
    },
    gold: {
      bg: getExtendedTokenColor(
        token,
        'gold1',
        token.colorWarningBg || STAT_COLOR_PRESETS.gold.bg,
      ),
      text: getExtendedTokenColor(token, 'gold6', STAT_COLOR_PRESETS.gold.text),
      icon: getExtendedTokenColor(token, 'gold6', STAT_COLOR_PRESETS.gold.icon),
    },
    lime: {
      bg: getExtendedTokenColor(token, 'lime1', STAT_COLOR_PRESETS.lime.bg),
      text: getExtendedTokenColor(token, 'lime6', STAT_COLOR_PRESETS.lime.text),
      icon: getExtendedTokenColor(token, 'lime6', STAT_COLOR_PRESETS.lime.icon),
    },
  };
}

export type StatColorPreset = keyof typeof STAT_COLOR_PRESETS;

/**
 * StatCard 组件属性
 */
export interface StatCardProps {
  /** 标题 */
  title: string;
  /** 数值 */
  value: string | number;
  /** 图标 */
  icon?: React.ReactNode;
  /** 颜色预设 */
  color?: StatColorPreset;
  /** 自定义颜色 */
  customColor?: { bg: string; text: string; icon: string };
  /** 加载中 */
  loading?: boolean;
  /** 后缀文本（如单位） */
  suffix?: string;
  /** 前缀文本 */
  prefix?: string;
  /** 描述文本 */
  description?: string;
  /** 点击回调 */
  onClick?: () => void;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 自定义类名 */
  className?: string;
}

/**
 * 统计卡片组件
 *
 * @example
 * ```tsx
 * <StatCard
 *   title="总用户数"
 *   value={1234}
 *   icon={<UserOutlined />}
 *   color="blue"
 *   suffix="人"
 * />
 * ```
 */
export const StatCard = React.memo(function StatCard({
  title,
  value,
  icon,
  color = 'blue',
  customColor,
  loading = false,
  suffix,
  prefix,
  description,
  onClick,
  style,
  className,
}: StatCardProps): React.ReactElement {
  const { token } = theme.useToken();
  const themedPresets = useMemo(() => getThemedColorPresets(token), [token]);
  const colors = customColor ?? themedPresets[color] ?? themedPresets.blue;

  if (loading) {
    return (
      <Card size="small" style={style} className={className}>
        <Skeleton active paragraph={{ rows: 1 }} />
      </Card>
    );
  }

  return (
    <Card
      size="small"
      hoverable={!!onClick}
      onClick={onClick}
      style={{
        background: colors.bg,
        borderColor: 'transparent',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
      className={className}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: token.marginMD }}
      >
        {icon && (
          <div
            style={{
              fontSize: token.fontSizeHeading2,
              color: colors.icon,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: token.fontSizeSM,
              color: token.colorTextTertiary,
              marginBottom: token.marginXXS,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: token.fontSizeHeading3,
              fontWeight: 600,
              color: colors.text,
              lineHeight: 1.2,
            }}
          >
            {prefix}
            {value}
            {suffix && (
              <span
                style={{
                  fontSize: token.fontSize,
                  fontWeight: 400,
                  marginLeft: token.marginXXS,
                }}
              >
                {suffix}
              </span>
            )}
          </div>
          {description && (
            <div
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorTextTertiary,
                marginTop: token.marginXXS,
              }}
            >
              {description}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
});

// ======================== StatCardGrid ========================

/**
 * StatCardGrid 配置项
 */
export interface StatCardGridItem extends StatCardProps {
  /** 唯一标识 */
  key: string;
  /** 是否隐藏 */
  hidden?: boolean;
  /** 列宽配置 (Ant Design Col 的 span 属性) */
  colSpan?: number;
  /** 响应式列宽配置 */
  colProps?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    xxl?: number;
  };
}

/**
 * StatCardGrid 组件属性
 */
export interface StatCardGridProps {
  /** 卡片数据 */
  items: StatCardGridItem[];
  /** 行间距，默认 [16, 16] */
  gutter?: number | [number, number];
  /** 默认列宽（当 item 未指定 colSpan 时使用），默认 6 */
  defaultColSpan?: number;
  /** 默认响应式列宽配置 */
  defaultColProps?: StatCardGridItem['colProps'];
  /** 全局加载状态 */
  loading?: boolean;
}

/**
 * 统计卡片网格组件
 *
 * @example
 * ```tsx
 * <StatCardGrid
 *   loading={loading}
 *   items={[
 *     { key: 'users', title: '总用户', value: 1234, color: 'blue', icon: <UserOutlined /> },
 *     { key: 'orders', title: '总订单', value: 567, color: 'green', icon: <ShoppingOutlined /> },
 *     { key: 'revenue', title: '总收入', value: '¥12,345', color: 'orange', icon: <DollarOutlined /> },
 *     { key: 'growth', title: '增长率', value: '12.5%', color: 'purple', icon: <RiseOutlined /> },
 *   ]}
 * />
 * ```
 */
export function StatCardGrid({
  items,
  gutter = [16, 16],
  defaultColSpan = 6,
  defaultColProps,
  loading = false,
}: StatCardGridProps): React.ReactElement {
  const visibleItems = items.filter((item) => !item.hidden);

  return (
    <Row gutter={gutter}>
      {visibleItems.map((item) => {
        const { key, hidden: _hidden, colSpan, colProps, ...cardProps } = item;

        return (
          <Col
            key={key}
            span={colSpan ?? defaultColSpan}
            {...(colProps ??
              defaultColProps ?? { xs: 24, sm: 12, md: 12, lg: 6 })}
          >
            <StatCard {...cardProps} loading={loading || cardProps.loading} />
          </Col>
        );
      })}
    </Row>
  );
}
